import React, { useRef, useState } from 'react';

const ImportExportModal = ({ onClose, onImport, onExport, onExportTemplate }) => {
  const fileInputRef = useRef();
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [pendingBatch, setPendingBatch] = useState(null);

  const handleFileChange = async (e) => {
    setImporting(true);
    setImportResult(null);
    setImportSummary(null);
    setPendingBatch(null);
    try {
      // onImport debe devolver un resumen: { count, errores, detalles: [{row, error}], productos: [...] }
      const result = await onImport(e.target.files[0], { previewOnly: true });
      if (result && typeof result === 'object' && ('productos' in result)) {
        setPendingBatch(result.productos);
        setImportSummary({ count: result.productos.length, errores: result.errores, detalles: result.detalles });
        setImportResult({ type: 'info', message: `Se detectaron ${result.productos.length} productos en el archivo. Confirma para importar.` });
      } else if (result && ('count' in result || 'errores' in result)) {
        setImportSummary(result);
        setImportResult({ type: result.errores === 0 ? 'success' : 'warning', message: `Importados: ${result.count}, Errores: ${result.errores}` });
      } else {
        setImportResult({ type: 'success', message: 'Importación completada correctamente.' });
      }
    } catch (err) {
      setImportResult({ type: 'error', message: 'Error al importar productos.' });
    }
    setImporting(false);
    fileInputRef.current.value = '';
  };

  const handleConfirmBatch = async () => {
    if (!pendingBatch) return;
    setImporting(true);
    setImportResult(null);
    try {
      // onImport debe aceptar { productos, batch: true }
      const result = await onImport(pendingBatch, { batch: true });
      setImportSummary(result);
      setImportResult({ type: result.errores === 0 ? 'success' : 'warning', message: `Importados: ${result.count}, Errores: ${result.errores}` });
      setPendingBatch(null);
    } catch (err) {
      setImportResult({ type: 'error', message: 'Error al importar productos.' });
    }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8 relative animate-fade-in">
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold" onClick={onClose}>&times;</button>
        <h2 className="text-2xl font-bold mb-2 text-blue-800 flex items-center gap-2">
          <span className="inline-block bg-blue-100 text-blue-700 rounded-full px-3 py-1 text-sm font-semibold">WMS</span>
          Importar / Exportar productos
        </h2>
        <p className="text-gray-600 mb-6">Administra el catálogo de productos de tu almacén de forma masiva. Puedes importar desde Excel, exportar todos los productos o descargar una plantilla de ejemplo.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Importar */}
          <div className="bg-gray-50 rounded-lg p-6 flex flex-col gap-4 border border-gray-200 shadow-sm">
            <h3 className="font-semibold text-lg mb-2 text-blue-700">Importar productos</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">Selecciona un archivo Excel (.xlsx) o CSV</label>
            <input ref={fileInputRef} type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" onChange={handleFileChange} className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            <button type="button" className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded font-semibold mt-2" onClick={onExportTemplate}>Descargar plantilla de importación</button>
            {importing && <div className="text-blue-600 mt-2">Importando productos...</div>}
            {importResult && <div className={`mt-2 p-2 rounded ${importResult.type === 'success' ? 'bg-green-100 text-green-800' : importResult.type === 'warning' ? 'bg-yellow-100 text-yellow-800' : importResult.type === 'info' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>{importResult.message}</div>}
            {importSummary && importSummary.detalles && importSummary.detalles.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto text-xs bg-yellow-50 border border-yellow-200 rounded p-2">
                <div className="font-semibold text-yellow-800 mb-1">Errores de importación:</div>
                <ul className="list-disc pl-5">
                  {importSummary.detalles.map((d, i) => (
                    <li key={i} className="text-yellow-900">Fila {d.row}: {d.error}</li>
                  ))}
                </ul>
              </div>
            )}
            {pendingBatch && (
              <button type="button" className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded font-semibold mt-4" onClick={handleConfirmBatch} disabled={importing}>
                Confirmar importación de {pendingBatch.length} productos
              </button>
            )}
            <ul className="text-xs text-gray-500 mt-2 list-disc pl-5">
              <li>La importación masiva permite crear o actualizar productos existentes.</li>
              <li>El campo <b>Código</b> debe ser único por cliente.</li>
              <li>Revisa la plantilla para conocer los campos requeridos.</li>
            </ul>
          </div>
          {/* Exportar */}
          <div className="bg-gray-50 rounded-lg p-6 flex flex-col gap-4 border border-gray-200 shadow-sm">
            <h3 className="font-semibold text-lg mb-2 text-blue-700">Exportar productos</h3>
            <p className="text-gray-600 text-sm">Descarga todos los productos registrados en el sistema en formato Excel para análisis, respaldo o edición masiva.</p>
            <button type="button" className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded font-semibold" onClick={onExport}>Exportar productos a Excel</button>
            <div className="text-xs text-gray-500 mt-2">Incluye todos los campos relevantes del producto.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExportModal;
